// Browser side of chat attachments: photos re-encoded through a canvas
// (downscaled, no EXIF), video location removed with a poster frame taken,
// uploaded through `sendBlob`.
import { ApiError, errorMessage } from "../api";
import type { SessionManager } from "../auth/session";
import { sendBlob } from "../blobUpload";
import {
  downscaleSteps,
  encodeTypes,
  fitWithin,
  IMAGE_MAX_PX,
  ImageMetadataError,
  IMAGE_QUALITY,
  keepsOriginal,
  POSTER_MAX_PX,
  sniffImageType,
  stripAnimationMetadata,
  stripVideoLocation,
  VideoMetadataError,
  type Media,
  type MediaKind,
  type Size,
} from "./media";
import type { Prepared, TrayDeps } from "./tray";

class PrepareError extends Error {}

/** Animations are read whole to strip their metadata; the server takes at most 20 MB anyway. */
const MAX_ANIMATION_BYTES = 64 * 1024 * 1024;

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** Draws `source` at `target`, halving on the way so a big photo doesn't alias. */
function draw(source: CanvasImageSource, from: Size, target: Size): HTMLCanvasElement {
  let current: CanvasImageSource = source;
  let canvas = document.createElement("canvas");
  for (const step of downscaleSteps(from, target)) {
    canvas = document.createElement("canvas");
    canvas.width = step.width;
    canvas.height = step.height;
    const context = canvas.getContext("2d");
    if (!context) throw new PrepareError("This browser can't prepare photos");
    context.imageSmoothingQuality = "high";
    context.drawImage(current, 0, 0, step.width, step.height);
    current = canvas;
  }
  return canvas;
}

/** A photo at most 2,048 px on the longer side, re-encoded as WebP (else PNG/JPEG) without metadata. */
async function prepareImage(file: File): Promise<Prepared> {
  const head = new Uint8Array(await file.slice(0, 32).arrayBuffer());
  // Some systems give no type; the content says what it is.
  const type = file.type || sniffImageType(head) || "";
  if (keepsOriginal(type, head)) {
    // An animation goes up as it is (a canvas would flatten it), without its metadata.
    if (file.size > MAX_ANIMATION_BYTES) throw new PrepareError("It's too large to send");
    const bytes = stripAnimationMetadata(new Uint8Array(await file.arrayBuffer()), type);
    return {
      blob: new Blob([bytes], { type }),
      kind: "image",
      query: {},
      contentType: type,
      poster: null,
    };
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new PrepareError("This browser can't read this photo's format");
  }
  try {
    const from = { width: bitmap.width, height: bitmap.height };
    const canvas = draw(bitmap, from, fitWithin(from, IMAGE_MAX_PX));
    for (const encoded of encodeTypes(type)) {
      const blob = await toBlob(canvas, encoded, IMAGE_QUALITY);
      // A browser that can't encode a type falls back to PNG; try the next.
      if (blob && blob.type === encoded) {
        return { blob, kind: "image", query: {}, contentType: encoded, poster: null };
      }
    }
    throw new PrepareError("This browser can't prepare photos");
  } finally {
    bitmap.close();
  }
}

/** Width, height, duration and a poster frame, when this browser can play the video. */
async function readVideo(file: Blob): Promise<{ size: Size; durationMs: number; poster: Blob | null } | null> {
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  const event = (name: string) =>
    new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("timeout")), 10_000);
      video.addEventListener(name, () => (clearTimeout(timer), resolve()), { once: true });
      video.addEventListener("error", () => (clearTimeout(timer), reject(new Error("error"))), { once: true });
    });
  try {
    const loaded = event("loadeddata");
    video.src = url;
    await loaded;
    const size = { width: video.videoWidth, height: video.videoHeight };
    const durationMs = Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0;
    if (!size.width || !size.height) return null;
    let poster: Blob | null = null;
    try {
      const seeked = event("seeked");
      video.currentTime = Math.min(0.5, video.duration / 2 || 0);
      await seeked;
      poster = await toBlob(draw(video, size, fitWithin(size, POSTER_MAX_PX)), "image/jpeg", 0.8);
    } catch {
      poster = null;
    }
    return { size, durationMs, poster };
  } catch {
    return null;
  } finally {
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  }
}

async function prepareVideo(file: File): Promise<Prepared> {
  const { blob } = await stripVideoLocation(file);
  const info = await readVideo(file);
  return {
    blob,
    kind: "video",
    query: info
      ? {
          width: info.size.width,
          height: info.size.height,
          ...(info.durationMs > 0 ? { duration_ms: info.durationMs } : {}),
        }
      : {},
    contentType: file.type || "application/octet-stream",
    poster: info?.poster ?? null,
  };
}

async function prepare(file: File, kind: MediaKind): Promise<Prepared> {
  if (kind === "image") return prepareImage(file);
  if (kind === "video") return prepareVideo(file);
  return {
    blob: file,
    kind: "file",
    query: { name: file.name || "file" },
    contentType: file.type || "application/octet-stream",
    poster: null,
  };
}

/** Why an attachment couldn't be prepared or uploaded. */
export function uploadErrorText(error: unknown): string {
  if (error instanceof PrepareError || error instanceof VideoMetadataError || error instanceof ImageMetadataError) {
    return error.message;
  }
  if (error instanceof ApiError) {
    switch (error.code) {
      case "quota_exceeded":
        return "You've used all your space for photos and files";
      case "media_too_large":
        return "It's too large to send";
      case "unsupported_media":
        return "This format isn't supported";
      case "media_disabled":
        return "This server doesn't take photos or files";
      case "not_writable":
        return "You can't send messages here";
    }
  }
  return errorMessage(error);
}

/** The tray's browser dependencies for one conversation. */
export function browserTrayDeps(session: SessionManager, conversationId: string): TrayDeps {
  const path = `/conversations/${encodeURIComponent(conversationId)}/media`;
  return {
    prepare,
    async upload(prepared, onProgress, signal) {
      const media = await sendBlob<Media>(session, {
        method: "POST",
        path,
        query: { kind: prepared.kind, ...prepared.query },
        body: prepared.blob,
        contentType: prepared.contentType,
        onProgress,
        signal,
      });
      if (!prepared.poster) return media;
      try {
        // Without a poster the video just shows no preview frame.
        return await sendBlob<Media>(session, {
          method: "PUT",
          path: `${path}/${encodeURIComponent(media.id)}/poster`,
          query: {},
          body: prepared.poster,
          contentType: prepared.poster.type || "image/jpeg",
          signal,
        });
      } catch (error) {
        if (signal.aborted) throw error;
        return media;
      }
    },
    preview: (file) => URL.createObjectURL(file),
    revoke: (url) => URL.revokeObjectURL(url),
    describe: uploadErrorText,
  };
}

/** Uploads a recorded voice clip (`duration_ms` is the recorder's measure, as the server takes it). */
export function uploadVoice(
  session: SessionManager,
  conversationId: string,
  clip: { blob: Blob; contentType: string; durationMs: number },
  onProgress: (fraction: number) => void,
  signal: AbortSignal,
): Promise<Media> {
  return sendBlob<Media>(session, {
    method: "POST",
    path: `/conversations/${encodeURIComponent(conversationId)}/media`,
    query: { kind: "audio", duration_ms: clip.durationMs },
    body: clip.blob,
    contentType: clip.contentType,
    onProgress,
    signal,
  });
}
